/* =====================================================================
 * SkillCreatePage — Owner 전용 공식 스킬 관리 페이지
 *
 * 목록 뷰: 카드 그리드, 카테고리 필터, 검색
 * 편집 뷰: 메타 폼 + 마크다운 에디터 + ZIP 보조 파일 관리
 * ===================================================================== */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Plus,
  ArrowLeft,
  Trash2,
  Pencil,
  Eye,
  EyeOff,
  FileText,
  FolderArchive,
  Search,
  Save,
  Send,
  Upload,
  FilePlus,
  X,
  Loader2,
  Sparkles,
  ChevronDown,
} from 'lucide-react';
import { useChatStore } from '@auw/stores';
import { useOfficialSkillStore } from '@auw/stores';
import type { OfficialSkillSummary, OutputFormat } from '@auw/api-client';
import {
  uploadOfficialSkillFile,
  createOfficialSkillInlineFile,
  deleteOfficialSkillFile,
  listOfficialSkillFiles,
  getOfficialSkillFileContent,
} from '@auw/api-client';
import { Badge } from '@auw/workspace-ui';
import { useFeatureFlags } from '@auw/feature-flags';

const OUTPUT_FORMATS: { id: OutputFormat; label: string }[] = [
  { id: 'auto', label: 'Auto' },
  { id: 'text', label: 'Text' },
  { id: 'image', label: 'Image' },
  { id: 'video', label: 'Video' },
  { id: 'docx', label: 'DOCX' },
  { id: 'pptx', label: 'PPTX' },
  { id: 'hwpx', label: 'HWPX' },
  { id: 'xlsx', label: 'XLSX' },
  { id: 'interactive_ui', label: 'Interactive UI' },
];

const CATEGORIES = [
  { id: 'all', labelKey: 'skillCreate.catAll' },
  { id: 'document', labelKey: 'skillCreate.catDocument' },
  { id: 'code', labelKey: 'skillCreate.catCode' },
  { id: 'data', labelKey: 'skillCreate.catData' },
  { id: 'image', labelKey: 'skillCreate.catImage' },
  { id: 'marketing', labelKey: 'skillCreate.catMarketing' },
  { id: 'research', labelKey: 'skillCreate.catResearch' },
  { id: 'translation', labelKey: 'skillCreate.catTranslation' },
  { id: 'automation', labelKey: 'skillCreate.catAutomation' },
  { id: 'education', labelKey: 'skillCreate.catEducation' },
] as const;

type EditorMode = 'list' | 'create' | 'edit';

interface InlineFile {
  filename: string;
  content: string;
  isNew?: boolean;
}

/* ================================================================== */
/* Main Component                                                      */
/* ================================================================== */

export const SkillCreatePage: React.FC = () => {
  const { t } = useTranslation('common');
  const addToast = useChatStore((s) => s.addToast);
  const {
    skills,
    isLoading,
    fetchAllSkills,
    createSkill,
    updateSkill,
    deleteSkill,
    toggleStatus,
    getContent,
  } = useOfficialSkillStore();

  /* P11-5j: skills admin feature gates.
   *  - `admin.skills.editor` controls create/edit affordances (the
   *    "+ 새 스킬" button + per-card edit pencil + ability to enter
   *    create/edit mode at all). When off the surface degrades to a
   *    read-only catalogue.
   *  - `admin.skills.lifecycle` controls status (publish / unpublish)
   *    + delete actions. Tenants that should not modify the skill
   *    lifecycle (e.g. fully-managed catalogue) hide these buttons. */
  const skillFlags = useFeatureFlags();
  const skillEditorEnabled = skillFlags.isEnabled('admin.skills.editor');
  const skillLifecycleEnabled = skillFlags.isEnabled('admin.skills.lifecycle');

  const [mode, setMode] = useState<EditorMode>('list');
  const [editingId, setEditingId] = useState<string | null>(null);

  // List filters
  const [catFilter, setCatFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Editor form state
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [formCategory, setFormCategory] = useState('document');
  const [formOutputFormat, setFormOutputFormat] = useState<OutputFormat>('auto');
  const [formFormat, setFormFormat] = useState<'md' | 'zip'>('md');
  const [formContent, setFormContent] = useState('');
  const [formStatus, setFormStatus] = useState<'draft' | 'active'>('draft');
  const [saving, setSaving] = useState(false);

  // ZIP supplementary files
  const [refFiles, setRefFiles] = useState<string[]>([]);
  const [assetFiles, setAssetFiles] = useState<string[]>([]);
  const [scriptFiles, setScriptFiles] = useState<string[]>([]);

  // Inline file editor
  const [inlineEditing, setInlineEditing] = useState<{
    dir: 'references' | 'scripts';
    filename: string;
    content: string;
    isNew: boolean;
  } | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadDir, setUploadDir] = useState<'references' | 'assets' | 'scripts'>('references');

  useEffect(() => {
    fetchAllSkills();
  }, [fetchAllSkills]);

  const filteredSkills = useMemo(() => {
    let result = skills;
    if (catFilter !== 'all') result = result.filter((s) => s.category === catFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (s) => s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q),
      );
    }
    return result;
  }, [skills, catFilter, searchQuery]);

  const resetForm = useCallback(() => {
    setFormName('');
    setFormDesc('');
    setFormCategory('document');
    setFormOutputFormat('auto');
    setFormFormat('md');
    setFormContent('');
    setFormStatus('draft');
    setRefFiles([]);
    setAssetFiles([]);
    setScriptFiles([]);
    setInlineEditing(null);
    setEditingId(null);
  }, []);

  const handleCreate = useCallback(() => {
    if (!skillEditorEnabled) return;
    resetForm();
    setMode('create');
  }, [resetForm, skillEditorEnabled]);

  const handleEdit = useCallback(
    async (skill: OfficialSkillSummary) => {
      if (!skillEditorEnabled) return;
      setEditingId(skill.skill_id);
      setFormName(skill.name);
      setFormDesc(skill.description);
      setFormCategory(skill.category);
      setFormOutputFormat(skill.output_format || 'auto');
      setFormFormat(skill.format);
      setFormStatus(skill.status);
      setMode('edit');

      try {
        const content = await getContent(skill.skill_id);
        setFormContent(content);
      } catch {
        setFormContent('');
      }

      try {
        const files = await listOfficialSkillFiles(skill.skill_id);
        setRefFiles(files.reference_keys);
        setAssetFiles(files.asset_keys);
        setScriptFiles(files.script_keys);
      } catch {
        setRefFiles([]);
        setAssetFiles([]);
        setScriptFiles([]);
      }
    },
    [getContent, skillEditorEnabled],
  );

  const handleBack = useCallback(() => {
    setMode('list');
    resetForm();
  }, [resetForm]);

  const handleSave = useCallback(
    async (publishStatus: 'draft' | 'active') => {
      if (!formName.trim()) {
        addToast({ type: 'error', title: t('skillCreate.nameRequired') });
        return;
      }
      setSaving(true);
      try {
        if (mode === 'create') {
          await createSkill({
            name: formName.trim(),
            description: formDesc.trim(),
            category: formCategory,
            format: formFormat,
            output_format: formOutputFormat,
            content: formContent,
            status: publishStatus,
          });
          addToast({
            type: 'success',
            title: publishStatus === 'active'
              ? t('skillCreate.published')
              : t('skillCreate.savedDraft'),
          });
        } else if (editingId) {
          await updateSkill(editingId, {
            name: formName.trim(),
            description: formDesc.trim(),
            category: formCategory,
            output_format: formOutputFormat,
            content: formContent,
            status: publishStatus,
          });
          addToast({ type: 'success', title: t('skillCreate.updated') });
        }
        setMode('list');
        resetForm();
      } catch (err) {
        addToast({ type: 'error', title: (err as Error).message });
      } finally {
        setSaving(false);
      }
    },
    [
      mode, editingId, formName, formDesc, formCategory, formOutputFormat, formFormat, formContent,
      createSkill, updateSkill, addToast, t, resetForm,
    ],
  );

  const handleDelete = useCallback(
    async (skillId: string) => {
      if (!skillLifecycleEnabled) return;
      try {
        await deleteSkill(skillId);
        addToast({ type: 'success', title: t('skillCreate.deleted') });
      } catch (err) {
        addToast({ type: 'error', title: (err as Error).message });
      }
    },
    [deleteSkill, addToast, t, skillLifecycleEnabled],
  );

  const handleToggleStatus = useCallback(
    async (skillId: string) => {
      if (!skillLifecycleEnabled) return;
      try {
        await toggleStatus(skillId);
      } catch (err) {
        addToast({ type: 'error', title: (err as Error).message });
      }
    },
    [toggleStatus, addToast, skillLifecycleEnabled],
  );

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!editingId || !e.target.files?.length) return;
      const file = e.target.files[0];
      try {
        const res = await uploadOfficialSkillFile(editingId, uploadDir, file);
        if (uploadDir === 'references') setRefFiles((prev) => [...prev, res.file_path]);
        else if (uploadDir === 'assets') setAssetFiles((prev) => [...prev, res.file_path]);
        else setScriptFiles((prev) => [...prev, res.file_path]);
        addToast({ type: 'success', title: t('skillCreate.fileUploaded') });
      } catch (err) {
        addToast({ type: 'error', title: (err as Error).message });
      }
      e.target.value = '';
    },
    [editingId, uploadDir, addToast, t],
  );

  const handleDeleteFile = useCallback(
    async (dir: string, filePath: string) => {
      if (!editingId) return;
      const filename = filePath.replace(`${dir}/`, '');
      try {
        await deleteOfficialSkillFile(editingId, dir, filename);
        if (dir === 'references') setRefFiles((prev) => prev.filter((f) => f !== filePath));
        else if (dir === 'assets') setAssetFiles((prev) => prev.filter((f) => f !== filePath));
        else setScriptFiles((prev) => prev.filter((f) => f !== filePath));
      } catch (err) {
        addToast({ type: 'error', title: (err as Error).message });
      }
    },
    [editingId, addToast],
  );

  const handleStartInline = useCallback((dir: 'references' | 'scripts') => {
    setInlineEditing({ dir, filename: '', content: '', isNew: true });
  }, []);

  const handleEditInlineFile = useCallback(
    async (dir: 'references' | 'scripts', filePath: string) => {
      if (!editingId) return;
      const filename = filePath.replace(`${dir}/`, '');
      try {
        const res = await getOfficialSkillFileContent(editingId, dir, filename);
        setInlineEditing({
          dir,
          filename,
          content: res.content ?? '',
          isNew: false,
        });
      } catch (err) {
        addToast({ type: 'error', title: (err as Error).message });
      }
    },
    [editingId, addToast],
  );

  const handleSaveInline = useCallback(async () => {
    if (!editingId || !inlineEditing || !inlineEditing.filename.trim()) return;
    try {
      const res = await createOfficialSkillInlineFile(
        editingId,
        inlineEditing.dir,
        inlineEditing.filename.trim(),
        inlineEditing.content,
      );
      const keyList = inlineEditing.dir === 'references' ? refFiles : scriptFiles;
      if (!keyList.includes(res.file_path)) {
        if (inlineEditing.dir === 'references') setRefFiles((prev) => [...prev, res.file_path]);
        else setScriptFiles((prev) => [...prev, res.file_path]);
      }
      setInlineEditing(null);
      addToast({ type: 'success', title: t('skillCreate.fileSaved') });
    } catch (err) {
      addToast({ type: 'error', title: (err as Error).message });
    }
  }, [editingId, inlineEditing, refFiles, scriptFiles, addToast, t]);

  /* ---- Render ---- */

  if (mode !== 'list') {
    return (
      <Wrap>
        <PageCard>
          <EditorHeader>
            <BackBtn onClick={handleBack}>
              <ArrowLeft size={16} /> {t('skillCreate.back')}
            </BackBtn>
            <EditorActions>
              <ActionButton onClick={() => handleSave('draft')} disabled={saving}>
                <Save size={14} />
                {t('skillCreate.saveDraft')}
              </ActionButton>
              <PublishButton onClick={() => handleSave('active')} disabled={saving}>
                {saving ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
                {t('skillCreate.publish')}
              </PublishButton>
            </EditorActions>
          </EditorHeader>

          <FormSection>
            <SectionTitle>{t('skillCreate.basicInfo')}</SectionTitle>
            <FormGrid>
              <FormField>
                <FormLabel>{t('skillCreate.skillName')}</FormLabel>
                <FormInput
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder={t('skillCreate.skillNamePlaceholder')}
                />
              </FormField>
              <FormField>
                <FormLabel>{t('skillCreate.description')}</FormLabel>
                <FormInput
                  value={formDesc}
                  onChange={(e) => setFormDesc(e.target.value)}
                  placeholder={t('skillCreate.descriptionPlaceholder')}
                />
              </FormField>
              <FormRow>
                <FormField>
                  <FormLabel>{t('skillCreate.category')}</FormLabel>
                  <SelectWrap>
                    <FormSelect
                      value={formCategory}
                      onChange={(e) => setFormCategory(e.target.value)}
                    >
                      {CATEGORIES.filter((c) => c.id !== 'all').map((c) => (
                        <option key={c.id} value={c.id}>
                          {t(c.labelKey)}
                        </option>
                      ))}
                    </FormSelect>
                    <SelectIcon><ChevronDown size={14} /></SelectIcon>
                  </SelectWrap>
                </FormField>
                {mode === 'create' && (
                  <FormField>
                    <FormLabel>{t('skillCreate.format')}</FormLabel>
                    <RadioGroup>
                      <RadioLabel $active={formFormat === 'md'}>
                        <input
                          type="radio"
                          checked={formFormat === 'md'}
                          onChange={() => setFormFormat('md')}
                        />
                        <FileText size={14} /> Markdown
                      </RadioLabel>
                      <RadioLabel $active={formFormat === 'zip'}>
                        <input
                          type="radio"
                          checked={formFormat === 'zip'}
                          onChange={() => setFormFormat('zip')}
                        />
                        <FolderArchive size={14} /> ZIP
                      </RadioLabel>
                    </RadioGroup>
                  </FormField>
                )}
              </FormRow>
              <FormField>
                <FormLabel>{t('skillCreate.outputFormat', 'Output Format')}</FormLabel>
                <SelectWrap>
                  <FormSelect
                    value={formOutputFormat}
                    onChange={(e) => setFormOutputFormat(e.target.value as OutputFormat)}
                  >
                    {OUTPUT_FORMATS.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.label}
                      </option>
                    ))}
                  </FormSelect>
                  <SelectIcon><ChevronDown size={14} /></SelectIcon>
                </SelectWrap>
              </FormField>
            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>{t('skillCreate.contentTitle')}</SectionTitle>
            <EditorSplit>
              <EditorPane>
                <PaneLabel>{t('skillCreate.editor')}</PaneLabel>
                <MarkdownTextarea
                  value={formContent}
                  onChange={(e) => setFormContent(e.target.value)}
                  placeholder={t('skillCreate.editorPlaceholder')}
                />
              </EditorPane>
              <EditorPane>
                <PaneLabel>{t('skillCreate.preview')}</PaneLabel>
                <PreviewPane>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {formContent || `*${t('skillCreate.previewEmpty')}*`}
                  </ReactMarkdown>
                </PreviewPane>
              </EditorPane>
            </EditorSplit>
          </FormSection>

          {(formFormat === 'zip' || (mode === 'edit' && editingId)) && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileUpload}
              />

              {/* Inline File Editor Modal */}
              {inlineEditing && (
                <InlineOverlay>
                  <InlineDialog>
                    <InlineHeader>
                      <InlineTitle>
                        {inlineEditing.isNew
                          ? t('skillCreate.newFile')
                          : t('skillCreate.editFile')}
                      </InlineTitle>
                      <CloseBtn onClick={() => setInlineEditing(null)}>
                        <X size={16} />
                      </CloseBtn>
                    </InlineHeader>
                    {inlineEditing.isNew && (
                      <FormInput
                        value={inlineEditing.filename}
                        onChange={(e) =>
                          setInlineEditing((prev) =>
                            prev ? { ...prev, filename: e.target.value } : prev,
                          )
                        }
                        placeholder={t('skillCreate.filenamePlaceholder')}
                        style={{ marginBottom: 12 }}
                      />
                    )}
                    <InlineTextarea
                      value={inlineEditing.content}
                      onChange={(e) =>
                        setInlineEditing((prev) =>
                          prev ? { ...prev, content: e.target.value } : prev,
                        )
                      }
                      placeholder={t('skillCreate.fileContentPlaceholder')}
                    />
                    <InlineActions>
                      <ActionButton onClick={() => setInlineEditing(null)}>
                        {t('skillCreate.cancel')}
                      </ActionButton>
                      <PublishButton
                        onClick={handleSaveInline}
                        disabled={!inlineEditing.filename.trim()}
                      >
                        <Save size={14} /> {t('skillCreate.saveFile')}
                      </PublishButton>
                    </InlineActions>
                  </InlineDialog>
                </InlineOverlay>
              )}

              <FileSectionGroup
                title={t('skillCreate.references')}
                files={refFiles}
                dir="references"
                canInline
                onUpload={() => {
                  setUploadDir('references');
                  fileInputRef.current?.click();
                }}
                onNewInline={() => handleStartInline('references')}
                onEditInline={(fp) => handleEditInlineFile('references', fp)}
                onDelete={(fp) => handleDeleteFile('references', fp)}
                t={t}
              />
              <FileSectionGroup
                title={t('skillCreate.assets')}
                files={assetFiles}
                dir="assets"
                canInline={false}
                onUpload={() => {
                  setUploadDir('assets');
                  fileInputRef.current?.click();
                }}
                onDelete={(fp) => handleDeleteFile('assets', fp)}
                t={t}
              />
              <FileSectionGroup
                title={t('skillCreate.scripts')}
                files={scriptFiles}
                dir="scripts"
                canInline
                onUpload={() => {
                  setUploadDir('scripts');
                  fileInputRef.current?.click();
                }}
                onNewInline={() => handleStartInline('scripts')}
                onEditInline={(fp) => handleEditInlineFile('scripts', fp)}
                onDelete={(fp) => handleDeleteFile('scripts', fp)}
                t={t}
              />
            </>
          )}
        </PageCard>
      </Wrap>
    );
  }

  /* ---- List View ---- */
  return (
    <Wrap>
      <PageCard>
        <ListHeader>
          <div>
            <CardTitle>
              <Sparkles size={20} />
              {t('skillCreate.pageTitle')}
            </CardTitle>
            <CardDesc>{t('skillCreate.pageDesc')}</CardDesc>
          </div>
          {skillEditorEnabled && (
            <CreateButton onClick={handleCreate}>
              <Plus size={16} /> {t('skillCreate.newSkill')}
            </CreateButton>
          )}
        </ListHeader>

        <ToolBar>
          <CatChips>
            {CATEGORIES.map((c) => (
              <CatChip
                key={c.id}
                $active={catFilter === c.id}
                onClick={() => setCatFilter(c.id)}
              >
                {t(c.labelKey)}
              </CatChip>
            ))}
          </CatChips>
          <SearchBox>
            <Search size={14} />
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('skillCreate.search')}
            />
          </SearchBox>
        </ToolBar>

        {isLoading ? (
          <LoadingWrap>
            <Loader2 size={20} className="spin" />
            {t('skillCreate.loading')}
          </LoadingWrap>
        ) : filteredSkills.length === 0 ? (
          <EmptyState>
            <EmptyIcon><Sparkles size={32} /></EmptyIcon>
            <EmptyText>{t('skillCreate.empty')}</EmptyText>
            {skillEditorEnabled && (
              <CreateButton onClick={handleCreate}>
                <Plus size={16} /> {t('skillCreate.newSkill')}
              </CreateButton>
            )}
          </EmptyState>
        ) : (
          <SkillGrid>
            {filteredSkills.map((skill) => (
              <SkillCard key={skill.skill_id}>
                <SkillCardTop>
                  <SkillIcon>
                    {skill.format === 'zip' ? (
                      <FolderArchive size={18} />
                    ) : (
                      <FileText size={18} />
                    )}
                  </SkillIcon>
                  <StatusBadge $status={skill.status}>
                    {skill.status === 'active'
                      ? t('skillCreate.statusActive')
                      : t('skillCreate.statusDraft')}
                  </StatusBadge>
                </SkillCardTop>
                <SkillName>{skill.name}</SkillName>
                <SkillCatBadge>{t(`skillCreate.cat${capitalize(skill.category)}`)}</SkillCatBadge>
                <SkillDesc>{skill.description || t('skillCreate.noDescription')}</SkillDesc>
                <SkillCardFooter>
                  <SkillMeta>
                    <FormatTag>{skill.format.toUpperCase()}</FormatTag>
                    <DateText>{formatDate(skill.created_at)}</DateText>
                  </SkillMeta>
                  <SkillActions>
                    {skillLifecycleEnabled && (
                      <IconBtn
                        title={
                          skill.status === 'active'
                            ? t('skillCreate.unpublish')
                            : t('skillCreate.publishBtn')
                        }
                        onClick={() => handleToggleStatus(skill.skill_id)}
                      >
                        {skill.status === 'active' ? <EyeOff size={14} /> : <Eye size={14} />}
                      </IconBtn>
                    )}
                    {skillEditorEnabled && (
                      <IconBtn title={t('skillCreate.edit')} onClick={() => handleEdit(skill)}>
                        <Pencil size={14} />
                      </IconBtn>
                    )}
                    {skillLifecycleEnabled && (
                      <IconBtn
                        title={t('skillCreate.delete')}
                        $danger
                        onClick={() => handleDelete(skill.skill_id)}
                      >
                        <Trash2 size={14} />
                      </IconBtn>
                    )}
                  </SkillActions>
                </SkillCardFooter>
              </SkillCard>
            ))}
          </SkillGrid>
        )}
      </PageCard>
    </Wrap>
  );
};

export default SkillCreatePage;

/* ================================================================== */
/* Sub-component: File Section                                         */
/* ================================================================== */

interface FileSectionGroupProps {
  title: string;
  files: string[];
  dir: string;
  canInline: boolean;
  onUpload: () => void;
  onNewInline?: () => void;
  onEditInline?: (fp: string) => void;
  onDelete: (fp: string) => void;
  t: (key: string) => string;
}

const FileSectionGroup: React.FC<FileSectionGroupProps> = ({
  title,
  files,
  dir,
  canInline,
  onUpload,
  onNewInline,
  onEditInline,
  onDelete,
  t,
}) => (
  <FormSection>
    <FileSectionHeader>
      <SectionTitle>{title}</SectionTitle>
      <FileSectionActions>
        <SmallBtn onClick={onUpload}>
          <Upload size={12} /> {t('skillCreate.uploadFile')}
        </SmallBtn>
        {canInline && onNewInline && (
          <SmallBtn onClick={onNewInline}>
            <FilePlus size={12} /> {t('skillCreate.writeNew')}
          </SmallBtn>
        )}
      </FileSectionActions>
    </FileSectionHeader>
    {files.length === 0 ? (
      <FileEmpty>{t('skillCreate.noFiles')}</FileEmpty>
    ) : (
      <FileList>
        {files.map((fp) => {
          const basename = fp.split('/').pop() || fp;
          const isText = /\.(md|txt|py|js|ts|sh|json|yaml|yml|csv)$/i.test(basename);
          return (
            <FileItem key={fp}>
              <FileName>{basename}</FileName>
              <FileActions>
                {canInline && isText && onEditInline && (
                  <IconBtn title={t('skillCreate.edit')} onClick={() => onEditInline(fp)}>
                    <Pencil size={12} />
                  </IconBtn>
                )}
                <IconBtn title={t('skillCreate.delete')} $danger onClick={() => onDelete(fp)}>
                  <Trash2 size={12} />
                </IconBtn>
              </FileActions>
            </FileItem>
          );
        })}
      </FileList>
    )}
  </FormSection>
);

/* ================================================================== */
/* Helpers                                                             */
/* ================================================================== */

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return dateStr;
  }
}

/* ================================================================== */
/* Styles                                                              */
/* ================================================================== */

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Wrap = styled.div`
  flex: 1;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px;

  .spin { animation: ${spin} 1s linear infinite; }

  @media (max-width: 768px) {
    padding: 16px 12px;
  }
`;

const PageCard = styled.main`
  width: 100%;
  max-width: 1000px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusLg};
  padding: 36px;

  @media (max-width: 768px) {
    padding: 20px 16px;
    border-radius: ${({ theme }) => theme.sizes.borderRadius};
  }
`;

/* ---- List View ---- */

const ListHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const CardTitle = styled.h1`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 4px;
  font-size: 1.375rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.02em;
`;

const CardDesc = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const CreateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.bgPrimary};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  white-space: nowrap;
  &:hover { opacity: 0.85; }
`;

const ToolBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const CatChips = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
`;

const CatChip = styled.button<{ $active: boolean }>`
  padding: 4px 12px;
  border-radius: 100px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid ${({ theme, $active }) =>
    $active ? theme.colors.accent : theme.colors.border};
  background: ${({ $active, theme }) =>
    $active ? theme.colors.accent : 'transparent'};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.bgPrimary : theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textMuted};
  min-width: 180px;
`;

const SearchInput = styled.input`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.8125rem;
  outline: none;
  width: 100%;
  font-family: inherit;
`;

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 60px 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.875rem;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 60px 0;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SkillGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
`;

const SkillCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: 20px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  display: flex;
  flex-direction: column;
  gap: 8px;
  transition: border-color 0.15s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderInput};
  }
`;

const SkillCardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SkillIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StatusBadge = styled.span<{ $status: string }>`
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 100px;
  ${({ $status }) =>
    $status === 'active'
      ? css`
          background: rgba(34, 197, 94, 0.12);
          color: #22c55e;
        `
      : css`
          background: rgba(234, 179, 8, 0.12);
          color: #eab308;
        `}
`;

const SkillName = styled.h3`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SkillCatBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 500;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
  color: ${({ theme }) => theme.colors.textSecondary};
  width: fit-content;
`;

const SkillDesc = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.4em;
`;

const SkillCardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const SkillMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FormatTag = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'};
  padding: 1px 6px;
  border-radius: 4px;
`;

const DateText = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const SkillActions = styled.div`
  display: flex;
  gap: 4px;
`;

const IconBtn = styled.button<{ $danger?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme, $danger }) =>
    $danger ? '#ef4444' : theme.colors.textMuted};
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme, $danger }) =>
      $danger ? '#dc2626' : theme.colors.textPrimary};
  }
`;

/* ---- Editor View ---- */

const EditorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const EditorActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
  }
  &:disabled { opacity: 0.5; cursor: default; }
`;

const PublishButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.accent};
  color: ${({ theme }) => theme.colors.bgPrimary};
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: default; }

  .spin { animation: ${spin} 1s linear infinite; }
`;

const FormSection = styled.section`
  margin-bottom: 28px;
`;

const SectionTitle = styled.h2`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0 0 14px;
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FormRow = styled.div`
  display: flex;
  gap: 14px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FormField = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FormLabel = styled.label`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const FormInput = styled.input`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.8125rem;
  outline: none;
  font-family: inherit;
  transition: border-color 0.15s;

  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const SelectWrap = styled.div`
  position: relative;
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 8px 32px 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.8125rem;
  outline: none;
  font-family: inherit;
  appearance: none;
  cursor: pointer;

  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const SelectIcon = styled.div`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const RadioLabel = styled.label<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  border: 1px solid ${({ $active, theme }) =>
    $active ? theme.colors.accent : theme.colors.border};
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.accent : theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.15s;

  input { display: none; }

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

/* ---- Markdown Editor ---- */

const EditorSplit = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  min-height: 400px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    min-height: 300px;
  }
`;

const EditorPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 0;
`;

const PaneLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const MarkdownTextarea = styled.textarea`
  flex: 1;
  min-height: 360px;
  padding: 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.8125rem;
  line-height: 1.6;
  resize: vertical;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const PreviewPane = styled.div`
  flex: 1;
  min-height: 360px;
  padding: 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'};
  overflow-y: auto;
  font-size: 0.8125rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textPrimary};

  h1,
  h2,
  h3 {
    margin: 1em 0 0.5em;
    &:first-child { margin-top: 0; }
  }

  p { margin: 0.5em 0; }

  code {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'};
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 0.8em;
  }

  pre {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)'};
    padding: 12px;
    border-radius: 8px;
    overflow-x: auto;
    code {
      background: none;
      padding: 0;
    }
  }

  table {
    border-collapse: collapse;
    width: 100%;
    th, td {
      border: 1px solid ${({ theme }) => theme.colors.border};
      padding: 6px 10px;
      text-align: left;
      font-size: 0.8125rem;
    }
    th {
      background: ${({ theme }) =>
        theme.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'};
      font-weight: 600;
    }
  }

  ul, ol {
    padding-left: 1.5em;
    margin: 0.5em 0;
  }
`;

/* ---- File Sections ---- */

const FileSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const FileSectionActions = styled.div`
  display: flex;
  gap: 6px;
`;

const SmallBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const FileEmpty = styled.p`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
  padding: 12px 0;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'};
`;

const FileName = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileActions = styled.div`
  display: flex;
  gap: 2px;
  flex-shrink: 0;
`;

/* ---- Inline Editor Overlay ---- */

const InlineOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const InlineDialog = styled.div`
  width: 100%;
  max-width: 640px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 80vh;
`;

const InlineHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const InlineTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.colors.bgHover};
  }
`;

const InlineTextarea = styled.textarea`
  flex: 1;
  min-height: 300px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.8125rem;
  line-height: 1.6;
  resize: vertical;
  outline: none;
  &:focus {
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const InlineActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;
